/**
 * pages/signup/steps/Dob.tsx — date-of-birth entry.
 *
 * The server-side age gate is authoritative — we still enforce
 * client-side via `isEighteenPlus` from the shared package for
 * immediate feedback. Rejection copy is terminal-ish and warm:
 * this user cannot open an account today, but they will be able to
 * on their 18th birthday.
 */

import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, Field } from "../../../components";
import { advance, isEighteenPlus } from "@aster/onboarding";
import { getProgress, setProgress } from "../signup-store";
import { SignupShell } from "../SignupShell";

export function SignupDob() {
  const navigate = useNavigate();
  const cur = getProgress();

  if (!cur || !cur.legal_name) {
    return <Navigate to="/signup/name" replace />;
  }

  const [dob, setDob] = useState<string>(cur.date_of_birth ?? "");
  const [err, setErr] = useState<string | null>(null);

  // The <input type=date> value IS `YYYY-MM-DD`. Perfect for the
  // shared helper — no parsing needed.
  const isValid = /^\d{4}-\d{2}-\d{2}$/.test(dob);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      setErr("Enter a valid date.");
      return;
    }
    if (!isEighteenPlus(dob)) {
      // Route to the terminal blocked screen. It will clear
      // progress on mount — the user's DOB isn't going to change
      // in a way that helps them today, and the server would
      // reject any re-attempt with the same DOB anyway.
      navigate("/signup/blocked", { state: { reason: "under_18" } });
      return;
    }
    setProgress(advance(cur, "address", { date_of_birth: dob }));
    navigate("/signup/address");
  };

  return (
    <SignupShell
      title="When were you born?"
      subtitle="We need this to verify your identity and confirm you're 18 or older — a regulatory requirement for opening any financial account."
      step={3}
      backTo="/signup/name"
      errorBanner={err}
    >
      <form onSubmit={onSubmit}>
        <Field
          label="Date of birth"
          type="date"
          value={dob}
          onChange={setDob}
          autoComplete="bday"
        />
        <Button type="submit" disabled={!isValid}>
          Continue
        </Button>
      </form>
    </SignupShell>
  );
}
